/**
 * Socket Context - Real-time communication management
 * Provides WebSocket connectivity with automatic reconnection,
 * error handling, and event management for webinar features.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import io from 'socket.io-client';
import { useAuth } from '../hooks/useAuth';
import { toUserFacingError } from '../utils/apiErrorHandler';

export const SocketContext = createContext();

// Custom hook for using socket context
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    // Return a default object for components rendered outside SocketProvider
    // This can happen during SSR or initial render
    return {
      socket: null,
      isConnected: false,
      connected: false,
      connectionState: CONNECTION_STATES.DISCONNECTED,
      error: null,
      emit: () => false,
      on: () => () => {},
      off: () => {},
      subscribe: () => () => {},
      connectAsGuest: () => {},
      disconnectGuest: () => {},
      isGuestConnection: false,
    };
  }

  // Map subscribe/unsubscribe to on/off for compatibility
  const socket = context.socket;
  // Memoize off so it's a stable reference (prevents infinite re-render loops in consumers)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const off = useCallback((event, callback) => {
    if (socket) {
      socket.off(event, callback);
    }
  }, [socket]);

  return {
    ...context,
    isConnected: context.connected,
    on: context.subscribe,
    off,
  };
};

// What a viewer reads when the socket cannot connect. Fixed copy on purpose:
// socket.io's own `err.message` is transport jargon and ConnectionStatus
// renders whatever is in `error` verbatim.
const SOCKET_ERROR_COPY = "Can't reach the server right now — trying to reconnect.";

// Connection states
export const CONNECTION_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
};

// Socket configuration
const SOCKET_CONFIG = {
  reconnection: true,
  reconnectionAttempts: 15,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 10000,
  timeout: 10000,
  // Start with polling, then upgrade to WebSocket. This is critical for
  // production behind Cloudflare/nginx: the initial WebSocket upgrade can
  // fail through reverse proxies, but HTTP long-polling always works.
  // Once the polling connection is established, Socket.IO transparently
  // upgrades to WebSocket for lower latency.
  transports: ['polling', 'websocket'],
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const [error, setError] = useState(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [isGuestConnection, setIsGuestConnection] = useState(false);
  const { currentUser, getTokens } = useAuth();

  // Refs for tracking current webinar and event listeners
  const currentWebinarRef = useRef(null);
  const eventListenersRef = useRef(new Map());
  const guestDataRef = useRef(null);
  // Latest user, for socket callbacks that must NOT re-run when the object
  // identity changes. Written during render so any effect in the same commit
  // already sees it.
  const currentUserRef = useRef(null);
  currentUserRef.current = currentUser;
  // Guards guest socket creation against re-entry — see connectAsGuest.
  const guestSocketRef = useRef(null);

  // Socket URL from environment or default
  const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

  // Connect as guest (for backstage/guest join pages)
  const connectAsGuest = useCallback((guestData) => {
    // Don't connect if already connected as user
    if (currentUserRef.current && guestSocketRef.current) {
      return;
    }

    // Reuse an existing guest socket — whether or not it has finished
    // connecting.
    //
    // The old guard was `socket && socket.connected`, which left a window open:
    // socket.connected is false for the whole handshake, and every caller
    // re-invokes this the moment `socket` state changes (their effects depend on
    // connectAsGuest, whose identity used to change with it). Landing in that
    // window built ANOTHER socket, which set state again, which re-invoked this
    // again — a guest reconnecting over and over under a fresh socket id each
    // time, which is what the server logs as `Guest "X" reconnected: a → b`.
    //
    // The ref is written synchronously at creation, so re-entry is caught even
    // before React has re-rendered.
    if (guestSocketRef.current) {
      return;
    }

    // Store guest data for reconnection
    guestDataRef.current = guestData;
    setIsGuestConnection(true);
    setConnectionState(CONNECTION_STATES.CONNECTING);
    setError(null);

    const newSocket = io(SOCKET_URL, {
      ...SOCKET_CONFIG,
      withCredentials: true,
      auth: {
        guest: true,
        guestName: guestData?.displayName || 'Guest',
        // The guest invite key (set by GuestJoin): the server checks it when
        // this socket joins backstage, goes on stage or sends video.
        guestInviteKey: (() => {
          try { return JSON.parse(sessionStorage.getItem('guestSetup') || '{}').inviteKey || undefined; } catch (_) { return undefined; }
        })(),
      },
    });

    newSocket.on('connect', () => {
      setConnectionState(CONNECTION_STATES.CONNECTED);
      setError(null);
      setReconnectAttempt(0);
    });

    newSocket.on('disconnect', (reason) => {
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
    });

    newSocket.on('connect_error', (err) => {
      console.error('[Socket] Guest connection error:', err.message);
      setConnectionState(CONNECTION_STATES.ERROR);
      // ConnectionStatus renders this verbatim, and socket.io's message is
      // transport jargon ("xhr poll error", "websocket error", "timeout") with
      // nothing a guest can act on. The raw cause stays in the log.
      setError(SOCKET_ERROR_COPY);
    });

    newSocket.on('reconnect_attempt', (attempt) => {
      setConnectionState(CONNECTION_STATES.RECONNECTING);
      setReconnectAttempt(attempt);
    });

    newSocket.on('reconnect', () => {
      setConnectionState(CONNECTION_STATES.CONNECTED);
      setReconnectAttempt(0);
    });

    newSocket.on('reconnect_failed', () => {
      console.warn('[Socket] Guest reconnection failed after max attempts — retrying in 15s');
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      setError(null);
      setTimeout(() => {
        if (newSocket && !newSocket.connected) {
          newSocket.connect();
        }
      }, 15000);
    });

    // Claimed synchronously, before the state update, so a re-invocation in the
    // same tick sees it and bails at the guard above.
    guestSocketRef.current = newSocket;
    setSocket(newSocket);

    // NOTE: no cleanup is returned. This is a useCallback, not an effect — a
    // returned function is just a return value and every caller discards it, so
    // the teardown that used to live here never ran. Guest sockets are torn down
    // by disconnectGuest and by the provider unmount effect below.

    // Stable identity: callers invoke this from effects that list it as a
    // dependency, so anything that changed per-render (`socket`, `currentUser`)
    // made those effects re-fire and reconnect the guest. Both are read through
    // refs instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SOCKET_URL]);

  // Disconnect guest. Clearing guestSocketRef is what re-arms connectAsGuest —
  // without it the guard above would refuse to build a socket ever again, and a
  // guest who left and came back would sit there permanently disconnected.
  const disconnectGuest = useCallback(() => {
    const active = guestSocketRef.current;
    if (!active) return;
    try { active.removeAllListeners(); } catch { /* already gone */ }
    try { active.disconnect(); } catch { /* already gone */ }
    guestSocketRef.current = null;
    setSocket(null);
    setIsGuestConnection(false);
    guestDataRef.current = null;
    setConnectionState(CONNECTION_STATES.DISCONNECTED);
  }, []);

  // Real teardown for the guest socket, which connectAsGuest cannot do itself.
  // Without this the socket outlives the page that opened it and stays in the
  // room until the server times it out.
  useEffect(() => () => {
    const active = guestSocketRef.current;
    if (!active) return;
    try { active.removeAllListeners(); } catch { /* already gone */ }
    try { active.disconnect(); } catch { /* already gone */ }
    guestSocketRef.current = null;
  }, []);

  // Initialize socket connection for authenticated users
  useEffect(() => {
    // Skip if guest connection is active
    if (isGuestConnection) {
      return;
    }

    // Only connect if user is logged in
    if (!currentUser) {
      if (socket && !isGuestConnection) {
        socket.disconnect();
        setSocket(null);
      }
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      return;
    }

    setConnectionState(CONNECTION_STATES.CONNECTING);
    setError(null);

    // auth as a FUNCTION, not a snapshot: socket.io calls it on every
    // connection attempt, so a reconnect after a token refresh carries the
    // current token. A captured `accessToken` would go stale the first time the
    // access token rotated, and the only thing that used to refresh it was
    // tearing the whole socket down — which is what this effect no longer does.
    const newSocket = io(SOCKET_URL, {
      ...SOCKET_CONFIG,
      withCredentials: true,
      auth: (cb) => cb({ token: getTokens().accessToken }),
    });

    // Connection events
    newSocket.on('connect', () => {
      setConnectionState(CONNECTION_STATES.CONNECTED);
      setError(null);
      setReconnectAttempt(0);

      // Read through the ref, not the closure: this effect is keyed on the user
      // ID alone, so a profile refresh that changes the name must still be
      // reflected here without recreating the socket.
      const u = currentUserRef.current;
      if (u) {
        newSocket.emit('authenticate', {
          id: u.id,
          name: `${u.firstName} ${u.lastName}`.trim() || u.email,
          email: u.email,
          role: u.role,
        });
      }

      // Rejoin current webinar if reconnecting
      if (currentWebinarRef.current) {
        newSocket.emit('join_webinar', { webinarId: currentWebinarRef.current });
      }
    });

    newSocket.on('disconnect', (reason) => {
      setConnectionState(CONNECTION_STATES.DISCONNECTED);

      // Handle specific disconnect reasons
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', (err) => {
      // Use warn instead of error - connection issues are expected when
      // the user is on non-webinar pages or the server is starting up
      console.warn('[Socket] Connection error:', err.message);
      setConnectionState(CONNECTION_STATES.ERROR);
      // See the guest handler above — transport jargon, not user copy.
      setError(SOCKET_ERROR_COPY);
    });

    newSocket.on('reconnect_attempt', (attempt) => {
      setConnectionState(CONNECTION_STATES.RECONNECTING);
      setReconnectAttempt(attempt);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      setConnectionState(CONNECTION_STATES.CONNECTED);
      setReconnectAttempt(0);
    });

    newSocket.on('reconnect_failed', () => {
      console.warn('[Socket] Reconnection failed after max attempts — retrying in 15s');
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      setError(null);

      // Auto-retry: reset the reconnection counter and try again after a delay.
      // Without this, the socket permanently dies after max attempts.
      setTimeout(() => {
        if (newSocket && !newSocket.connected) {
          console.log('[Socket] Auto-retry: attempting reconnection');
          newSocket.connect();
        }
      }, 15000);
    });

    // Authentication response
    newSocket.on('authenticated', (data) => {
      if (data.success) {
      }
    });

    // Error handling
    newSocket.on('error', (data) => {
      console.error('[Socket] Server error:', data.message);
      // ConnectionStatus renders this verbatim — gate what the server sent.
      setError(toUserFacingError(data, SOCKET_ERROR_COPY));
    });

    setSocket(newSocket);

    // Cleanup on unmount or when a DIFFERENT user signs in
    return () => {
      newSocket.removeAllListeners();
      newSocket.disconnect();
      setSocket(null);
      eventListenersRef.current.clear();
    };
    // Keyed on the user ID, deliberately NOT the currentUser object.
    //
    // AuthContext replaces currentUser with a brand-new object on every profile
    // load — initAuth, refreshUser (Dashboard, MyAccount, AIScriptGenerator),
    // and the token-refresh path all call storeUser. Depending on the object
    // meant each of those tore this socket down and built a new one, which the
    // server sees as the host LEAVING a live session: it logs "Host
    // disconnected — starting 30s grace period", drops them out of the WebRTC
    // room, and guests lose them mid-broadcast. Nothing about the connection
    // needs to change when a name or plan field is refetched; only a different
    // person signing in does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, getTokens, SOCKET_URL]);

  // Subscribe to socket events
  const subscribe = useCallback((event, callback) => {
    if (!socket) return () => {};

    socket.on(event, callback);

    // Track listener for cleanup
    if (!eventListenersRef.current.has(event)) {
      eventListenersRef.current.set(event, []);
    }
    eventListenersRef.current.get(event).push(callback);

    // Return unsubscribe function
    return () => {
      socket.off(event, callback);
      const listeners = eventListenersRef.current.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }, [socket]);

  // Emit event with connection check
  const emit = useCallback((event, data) => {
    if (!socket || connectionState !== CONNECTION_STATES.CONNECTED) {
      console.warn('[Socket] Cannot emit, not connected');
      return false;
    }
    socket.emit(event, data);
    return true;
  }, [socket, connectionState]);

  // ===========================================
  // Webinar Room Management
  // ===========================================

  const joinWebinar = useCallback((webinarId) => {
    if (!webinarId) return false;

    currentWebinarRef.current = webinarId;
    return emit('join_webinar', { webinarId });
  }, [emit]);

  const leaveWebinar = useCallback((webinarId) => {
    if (!webinarId) webinarId = currentWebinarRef.current;
    if (!webinarId) return false;

    currentWebinarRef.current = null;
    return emit('leave_webinar', webinarId);
  }, [emit]);

  // ===========================================
  // Chat Functions
  // ===========================================

  const sendChatMessage = useCallback((webinarId, message, options = {}) => {
    const { isQuestion = false, isPrivate = false } = options;

    return emit('chat_message', {
      webinarId: webinarId || currentWebinarRef.current,
      message,
      isQuestion,
      isPrivate,
    });
  }, [emit]);

  const sendPrivateMessage = useCallback((webinarId, targetUserId, message) => {
    return emit('private_message', {
      webinarId: webinarId || currentWebinarRef.current,
      targetUserId,
      message,
    });
  }, [emit]);

  // ===========================================
  // Q&A Functions
  // ===========================================

  const submitQuestion = useCallback((webinarId, question) => {
    return emit('submit_question', {
      webinarId: webinarId || currentWebinarRef.current,
      question,
    });
  }, [emit]);

  const answerQuestion = useCallback((webinarId, questionId, answer, status = 'answered') => {
    return emit('answer_question', {
      webinarId: webinarId || currentWebinarRef.current,
      questionId,
      answer,
      status,
    });
  }, [emit]);

  // ===========================================
  // Poll Functions
  // ===========================================

  const createPoll = useCallback((webinarId, question, options, duration = 60) => {
    return emit('create_poll', {
      webinarId: webinarId || currentWebinarRef.current,
      question,
      options,
      duration,
    });
  }, [emit]);

  const votePoll = useCallback((webinarId, pollId, optionId) => {
    return emit('vote_poll', {
      webinarId: webinarId || currentWebinarRef.current,
      pollId,
      optionId,
    });
  }, [emit]);

  const closePoll = useCallback((webinarId, pollId) => {
    return emit('close_poll', {
      webinarId: webinarId || currentWebinarRef.current,
      pollId,
    });
  }, [emit]);

  // Legacy poll functions for backward compatibility
  const activatePoll = useCallback((sessionId, pollId, pollData) => {
    return emit('activate-poll', { webinarId: sessionId, pollId, pollData });
  }, [emit]);

  const submitPollResponse = useCallback((sessionId, pollId, userId, optionId) => {
    return emit('poll-response', { webinarId: sessionId, pollId, userId, optionId });
  }, [emit]);

  // ===========================================
  // Resource Functions
  // ===========================================

  const shareResource = useCallback((webinarId, resource) => {
    return emit('share_resource', {
      webinarId: webinarId || currentWebinarRef.current,
      resource,
    });
  }, [emit]);

  // Legacy resource function
  const displayResource = useCallback((sessionId, resourceId, resourceData) => {
    return emit('display-resource', { webinarId: sessionId, resourceId, resourceData });
  }, [emit]);

  // ===========================================
  // Webinar Status
  // ===========================================

  const updateWebinarStatus = useCallback((webinarId, status) => {
    return emit('webinar_status_update', {
      webinarId: webinarId || currentWebinarRef.current,
      status,
    });
  }, [emit]);

  // ===========================================
  // Utility Functions
  // ===========================================

  const reconnect = useCallback(() => {
    if (socket) {
      socket.connect();
    }
  }, [socket]);

  const disconnect = useCallback(() => {
    if (socket) {
      socket.disconnect();
    }
  }, [socket]);

  // Context value
  const value = {
    // Connection state
    socket,
    connected: connectionState === CONNECTION_STATES.CONNECTED,
    connectionState,
    error,
    reconnectAttempt,

    // Core functions
    subscribe,
    emit,
    reconnect,
    disconnect,

    // Guest connection
    connectAsGuest,
    disconnectGuest,
    isGuestConnection,

    // Webinar room
    joinWebinar,
    leaveWebinar,
    currentWebinarId: currentWebinarRef.current,

    // Chat
    sendChatMessage,
    sendPrivateMessage,

    // Q&A
    submitQuestion,
    answerQuestion,

    // Polls
    createPoll,
    votePoll,
    closePoll,
    activatePoll, // Legacy
    submitPollResponse, // Legacy

    // Resources
    shareResource,
    displayResource, // Legacy

    // Webinar status
    updateWebinarStatus,

    // Constants
    CONNECTION_STATES,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketProvider;
